import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Loader2, Save } from "lucide-react";
import { api, fmtErr } from "@/lib/api";
import { useCachedGet } from "@/lib/cache";

function Row({ c, value, onChange }) {
  const cap = value === "" ? null : Number(value);
  const taken = c.used + c.pending;
  const pct = cap ? Math.min(100, (100 * taken) / cap) : 0;
  const over = cap != null && taken >= cap;
  return (
    <tr data-testid={`alloc-row-${c.client_id}`}>
      <td className="px-4 py-3 font-medium text-slate-900 max-w-[240px] truncate">{c.name}</td>
      <td className="px-3 tabular-nums text-right" data-testid={`alloc-used-${c.client_id}`}>{c.used}</td>
      <td className="px-3 tabular-nums text-right text-slate-500">{c.pending}</td>
      <td className="px-3 w-36">
        <input type="number" min="0" value={value} placeholder="No limit" onChange={(e) => onChange(e.target.value)} data-testid={`alloc-input-${c.client_id}`}
          className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-slate-200 focus:border-indigo-500 outline-none tabular-nums" />
      </td>
      <td className="px-3 min-w-[160px]">
        {cap == null ? <span className="text-xs text-slate-400">Shares workspace pool</span> : (
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 rounded-full bg-slate-100"><div className={`h-full rounded-full ${over ? "bg-rose-500" : "bg-indigo-500"}`} style={{ width: `${pct}%` }} /></div>
            <span className={`text-xs tabular-nums ${over ? "text-rose-600 font-medium" : "text-slate-500"}`}>{Math.max(0, cap - taken)} left</span>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function ClientAllocations() {
  const [data, reload] = useCachedGet("/license/clients");
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data) setVals(Object.fromEntries(data.clients.map((c) => [c.client_id, c.allocation ?? ""])));
  }, [data]);
  if (!data) return null;
  const total = Object.values(vals).reduce((a, v) => a + (v === "" ? 0 : Number(v) || 0), 0);
  const overAllocated = !data.unlimited && data.allowance != null && total > data.allowance;
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/license/clients", { allocations: Object.fromEntries(Object.entries(vals).map(([k, v]) => [k, v === "" ? null : Number(v)])) });
      toast.success("Client allocations saved"); reload();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="tp-card" data-testid="client-allocations">
      <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-[color:var(--bd-sub)]">
        <Building2 className="w-4 h-4 text-indigo-500" />
        <div className="flex-1 min-w-[240px]">
          <div className="font-display font-semibold">Allocation by client <span className="text-xs font-normal text-slate-500">(optional)</span></div>
          <p className="text-xs text-slate-500">Set a monthly cap for a client to stop one account using up the whole plan. Leave blank for no limit.</p>
        </div>
        <span className={`text-xs tabular-nums ${overAllocated ? "text-amber-700" : "text-slate-500"}`} data-testid="alloc-total">
          {total} allocated{data.unlimited ? "" : ` of ${data.allowance} / mo`}{overAllocated ? " · more than your plan includes" : ""}
        </span>
        <button type="button" onClick={save} disabled={busy || !data.clients.length} data-testid="alloc-save"
          className="inline-flex items-center gap-2 px-3.5 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50 transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      </div>
      {data.clients.length === 0 ? <div className="px-5 py-6 text-sm text-slate-500" data-testid="alloc-empty">Add clients to allocate interviews per client.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-4 py-2.5">Client</th><th className="text-right px-3">Used</th><th className="text-right px-3">Pending</th><th className="text-left px-3">Monthly cap</th><th className="text-left px-3">This month</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.clients.map((c) => <Row key={c.client_id} c={c} value={vals[c.client_id] ?? ""} onChange={(v) => setVals({ ...vals, [c.client_id]: v })} />)}
            </tbody>
          </table>
        </div>
      )}
      <p className="px-5 py-3 text-xs text-slate-500 border-t border-[color:var(--bd-sub)]">Pending = invited but not started yet; they're reserved against the cap. Caps reset with your plan each month.</p>
    </div>
  );
}
